/**
 * "Say a bit more" nudge for a first message that is only a greeting or a compliment.
 *
 * The server decides (POST /v1/thread/{id}/opener-nudge). The nudge is advisory: any
 * error or timeout lets the unchanged draft send, and "Send anyway" always sends.
 */

const CHECK_TIMEOUT_MS = 2500;

// Literal icon keys keep the names visible to scripts/generate-lucide-icons.js.
const KIND_ICONS = {
  language: { icon: 'graduation-cap' },
  interest: { icon: 'sparkles' },
  place: { icon: 'map-pin' },
  generic: { icon: 'message-circle' },
};

// Sentence marks and separators the draft ends with. The Greek question mark is ";" or U+037E.
const TRAILING_MARKS = /[\s.!?…。！？؟;\u037E।,:،、，~～〜]+$/u;
const QUESTION_MARK = /[?？؟]/u;
// ";" asks only in a locale whose question template uses it, so an English "hi;" stays a greeting.
const LOCALE_QUESTION_MARK = /[;\u037E]/u;
const LEADING_INVERTED = /^[¡¿]+/u;
const SENTENCE_END = /[.!?…。！？]$/u;
// A draft's last clause starts after its last mark: "hola, ¿cómo estás" -> "cómo estás".
const CLAUSE_BREAK = /[.,!?;:¡¿…。！？，、،؟]/u;
// One emoji unit or emoticon per repetition: a nested quantifier here backtracks exponentially.
const TRAILING_EMOJI = /(?:\s|\p{Extended_Pictographic}|\p{Emoji_Modifier}|\u200D|\uFE0F|\p{Regional_Indicator}|[\u{E0020}-\u{E007F}]|[0-9#*]\uFE0F?\u20E3|\^_\^|<3+|8\)|[:;=]-?['’]?[/|*\]]|[xX]D+|\^\^|[:;=]-?(?:\)+|\(+|[DPp3]+))+$/u;
const NO_WORD_BREAK_SCRIPT = /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}]/u;
const DEFAULT_TEMPLATES = {
  named: '%greeting% %name%! %question%',
  unnamed: '%greeting%! %question%',
  questionNamed: '%greeting% %name%? %question%',
  questionUnnamed: '%greeting%? %question%',
};

function containsWord(text, word) {
  const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(?:^|[^\\p{L}\\p{N}_])${escaped}(?![\\p{L}\\p{N}_])`, 'iu').test(text);
}

// The server compares names without apostrophes, and Han and kana put a name right next to the greeting.
function mentions(text, token) {
  const plain = (value) => value.toLocaleLowerCase().replace(/ı/gu, 'i').normalize('NFD').replace(/\p{Mn}+/gu, '').normalize('NFC')
    .replace(/['’]/gu, '');
  return NO_WORD_BREAK_SCRIPT.test(token)
    ? plain(text).toLocaleLowerCase().includes(plain(token).toLocaleLowerCase())
    : containsWord(plain(text), plain(token));
}

/**
 * Splits off the last clause, so a template wraps only that: "hola, cómo estás" -> ["hola,", "cómo estás"].
 */
function splitLastClause(text) {
  const chars = [...text];
  for (let i = chars.length - 1; i >= 0; i -= 1) {
    // The ":" of ":)" or ";-)" is an emoticon, not a break.
    const emoticon = /[:;=]/u.test(chars[i]) && /^-?['’]?[()DPp3/|*\]]/u.test(chars.slice(i + 1, i + 4).join(''));
    if (CLAUSE_BREAK.test(chars[i]) && !emoticon) {
      const clause = chars.slice(i + 1).join('').trim();
      return /\p{L}/u.test(clause) ? [chars.slice(0, i + 1).join('').trim(), clause] : ['', text];
    }
  }
  return ['', text];
}

function fillTemplate(template, values) {
  return template.replace(/%(greeting|name|question)%/g, (match, key) => values[key]);
}

function capitalizeFirstLetter(text) {
  const index = text.search(/\p{L}/u);
  if (index < 0) {
    return text;
  }
  const letter = String.fromCodePoint(text.codePointAt(index));
  const upper = letter.toLocaleUpperCase(document.documentElement?.lang || undefined);
  // "ß" and ligatures grow when upper-cased. Title case keeps them as they are.
  const title = [...upper].length === 1 ? upper : letter;
  return `${text.slice(0, index)}${title}${text.slice(index + letter.length)}`;
}

/**
 * "hey" + "How are you?" + "Lena" -> "Hey Lena! How are you?", joined by the locale's template.
 * The name makes the opener differ per recipient: automod clusters identical openers.
 * A greeting that already holds one of nameTokens (a word of the name, or the username) gets no name.
 * asksWithoutMark comes from the server: "how are you" asks with no question mark.
 */
export function composeWithQuestion(draft, question, name = '', templates = {}, nameTokens = [], asksWithoutMark = false) {
  let greeting = String(draft || '').replace(/\s+/gu, ' ').trim();
  let marks = '';
  let emoji = '';
  for (let before = ''; before !== greeting;) {
    before = greeting;
    const endMarks = (greeting.match(TRAILING_MARKS) || [''])[0];
    greeting = greeting.slice(0, greeting.length - endMarks.length);
    const unit = (greeting.match(TRAILING_EMOJI) || [''])[0];
    greeting = greeting.slice(0, greeting.length - unit.length);
    [marks, emoji] = [`${endMarks}${marks}`, `${unit}${emoji}`];
  }
  const tail = emoji.replace(/\s+/gu, ' ').trim();
  const trimmedName = String(name || '').trim();
  if (!/\p{L}/u.test(greeting)) {
    // A draft with no letter follows the name, and the name goes in exactly as sent.
    const rest = [greeting.trim(), tail, question].filter(Boolean).join(' ');
    return trimmedName === ''
      ? rest
      : fillTemplate(templates.unnamed || DEFAULT_TEMPLATES.unnamed, { greeting: trimmedName, name: '', question: rest });
  }

  let [head, clause] = splitLastClause(greeting);
  // A "¡" or "¿" the head opens and does not close spans the whole greeting: "¡Hola, buenos días!".
  if (/[¡¿][^!?]*$/u.test(head) && !/[¡¿]$/u.test(head)) {
    [head, clause] = ['', greeting];
  }
  let inverted = '';
  if (head === '' && LEADING_INVERTED.test(clause)) {
    inverted = clause.match(LEADING_INVERTED)[0].slice(0, 1);
    clause = clause.replace(LEADING_INVERTED, '').trim();
  } else if (/[¡¿]+$/u.test(head)) {
    inverted = head.match(/[¡¿]+$/u)[0].slice(0, 1);
    head = head.replace(/[¡¿]+$/u, '').trim();
  }
  const tokens = [trimmedName, ...nameTokens].map((token) => String(token || '').trim()).filter(Boolean);
  const named = trimmedName !== '' && !tokens.some((token) => mentions(greeting, token));
  const localeAsks = LOCALE_QUESTION_MARK.test(templates.questionNamed || '') && LOCALE_QUESTION_MARK.test(marks);
  // The server's flag and the marks both describe the last clause, the part the template wraps.
  // A sender's own "!" says the clause is not a question: "how cute!".
  const exclaims = /[!！]/u.test(marks) && !QUESTION_MARK.test(marks) && inverted !== '¿';
  const asks = !exclaims && (asksWithoutMark || inverted === '¿' || QUESTION_MARK.test(marks) || localeAsks);
  const key = asks ? (named ? 'questionNamed' : 'questionUnnamed') : (named ? 'named' : 'unnamed');
  const template = templates[key] || DEFAULT_TEMPLATES[key];

  // A template that opens with "¡" or "¿" brings its own. Otherwise the sender's mark stays.
  // The sender's "¡" turns to "¿" when the template closes the clause with "?".
  if (!LEADING_INVERTED.test(template)) {
    clause = `${asks && inverted === '¡' ? '¿' : inverted}${clause}`;
  }
  const clauseStartsSentence = head === '' || SENTENCE_END.test(head);
  const composed = fillTemplate(template, {
    greeting: clauseStartsSentence ? capitalizeFirstLetter(clause) : clause,
    name: trimmedName,
    question: tail === '' ? question : `${tail} ${question}`,
  });
  if (head === '') {
    return composed;
  }
  // A full-width mark carries its own spacing: "你好，最近好吗".
  const joiner = /[、。！？，：；]$/u.test(head) || (/,$/u.test(head) && NO_WORD_BREAK_SCRIPT.test(clause)) ? '' : ' ';
  return `${capitalizeFirstLetter(head)}${joiner}${composed}`;
}

/**
 * Splits the "Send “%text%” anyway" template, so the sender's text never passes through i18n substitution.
 */
export function splitAnywayTemplate(template) {
  const parts = String(template || '%text%').split('%text%');
  return parts.length < 2 ? [`${parts[0]} `, ''] : [parts[0], parts.slice(1).join('%text%')];
}

/**
 * Remembers the one draft the sender chose to send unchanged, per thread.
 */
export function createBypass() {
  let entry = null;
  return {
    allow(threadId, text) {
      entry = { threadId: String(threadId), text };
    },
    consume(threadId, text) {
      if (entry && entry.threadId === String(threadId) && entry.text === text) {
        entry = null;
        return true;
      }
      return false;
    },
  };
}

export class OpenerNudge {
  constructor({ root, input, submit, apiClient, live = null, threadIdInput = null, messagesPane = null }) {
    this.root = root;
    this.input = input;
    this.submit = submit;
    this.apiClient = apiClient;
    this.live = live;
    this.threadIdInput = threadIdInput;
    this.title = root.querySelector('[data-opener-nudge-title]');
    this.prompt = root.querySelector('[data-opener-nudge-prompt]');
    this.chips = root.querySelector('[data-opener-nudge-chips]');
    this.anyway = root.querySelector('[data-opener-nudge-anyway]');
    this.anywayLabel = root.querySelector('[data-opener-nudge-anyway-label]');
    this.bypass = createBypass();
    this.pending = false;
    this.resendAfterCheck = null;
    this.openFor = null;
    this.name = '';
    this.nameTokens = [];
    this.asks = false;

    this.anyway.addEventListener('click', () => this.sendAnyway());
    this.input.addEventListener('input', () => this.hide());
    const onEscape = (e) => {
      // Safari sends keyCode 229 for the Escape that ends an IME composition.
      if (e.key === 'Escape' && !e.isComposing && e.keyCode !== 229 && this.isOpen()) {
        e.preventDefault();
        this.hide();
        this.input.focus();
      }
    };
    // The form holds the input, the mic, Send and the nudge, so Escape works from any of them.
    (this.input.form || this.input).addEventListener('keydown', onEscape);
    if (!this.input.form) {
      this.root.addEventListener('keydown', onEscape);
    }
    const onThreadChange = () => {
      this.resendAfterCheck = null;
      this.hide();
    };
    // A thread-list rebuild fires pm:thread-opened again for the open thread. The nudge stays for the
    // same draft, and closes when the contact is no longer active (a badge change fires it too).
    document.addEventListener('pm:thread-opened', () => {
      const held = this.openFor || this.resendAfterCheck;
      const row = held ? document.getElementById?.(`thread_${held.threadId}`) : null;
      const status = row?.getAttribute?.('data-contact-badge-status') || 'active';
      if (!held || status !== 'active' || !this.isStillComposing(held.threadId, held.text)) {
        onThreadChange();
      }
    });
    document.addEventListener('pm:thread-closed', onThreadChange);

    // Audio, photo and incoming messages all end the "first message" state without an input event.
    if (messagesPane && typeof MutationObserver !== 'undefined') {
      new MutationObserver((records) => {
        if (this.isOpen() && records.some((r) => Array.from(r.addedNodes).some(isMessageNode))) {
          this.hide();
        }
      }).observe(messagesPane, { childList: true, subtree: true });
    }
  }

  activeThreadId() {
    return this.threadIdInput ? String(this.threadIdInput.value) : null;
  }

  isOpen() {
    return !this.root.hidden;
  }

  /**
   * Resolves true when the message can go out now, false when the nudge holds it.
   */
  async allowSend({ threadId, text }) {
    if (this.isOpen()) {
      this.signalHeld();
      return false;
    }
    const maxLength = Number(this.root.dataset?.maxDraftLength) || Infinity;
    if (!threadId || [...text].length > maxLength || !isEmptyThread() || this.bypass.consume(threadId, text)) {
      return true;
    }
    if (this.pending) {
      // Sent again during the check: send it once the check settles, if nothing changed since.
      this.resendAfterCheck = { threadId, text };
      return false;
    }

    this.pending = true;
    this.submit.setAttribute('aria-busy', 'true');
    this.submit.classList.add('opacity-60');
    let allowed = false;
    try {
      const nudge = await this.fetchNudge(threadId, text);
      // The sender typed, switched threads or attached a photo during the check: hold, or the send
      // would go to the wrong thread, drop the newer text or leave the photo behind.
      if (!this.isStillComposing(threadId, text)) {
        return false;
      }
      if (!nudge || !nudge.nudge || !isEmptyThread()) {
        allowed = true;
        return true;
      }
      this.show(threadId, text, nudge);
      return false;
    } catch {
      allowed = this.isStillComposing(threadId, text);
      return allowed;
    } finally {
      this.pending = false;
      this.submit.removeAttribute('aria-busy');
      this.submit.classList.remove('opacity-60');
      const again = this.resendAfterCheck;
      this.resendAfterCheck = null;
      if (again && !allowed && !this.isOpen() && this.isStillComposing(again.threadId, again.text)) {
        setTimeout(() => window.send_msg?.(), 0);
      }
    }
  }

  isStillComposing(threadId, text) {
    const active = this.activeThreadId();
    const photos = window.photoUpload?.getPhotoQueue?.() || [];
    return this.input.value.trim() === text
      && (active === null || active === String(threadId))
      && photos.length === 0;
  }

  /**
   * Resolves null on a non-ok response or a timeout, and rejects on a network or parse error.
   * The timer races the whole request, a token refresh included.
   */
  async fetchNudge(threadId, text) {
    const body = new URLSearchParams({ message: text });
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    let timer = null;
    const timeout = new Promise((resolve) => {
      timer = setTimeout(() => {
        controller?.abort();
        resolve(null);
      }, CHECK_TIMEOUT_MS);
    });
    const request = this.apiClient.request(`/thread/${encodeURIComponent(threadId)}/opener-nudge`, {
      method: 'POST',
      body,
      signal: controller ? controller.signal : undefined,
    }).then((response) => (response.ok ? response.json() : null));
    try {
      return await Promise.race([request, timeout]);
    } finally {
      clearTimeout(timer);
      request.catch(() => {});
    }
  }

  show(threadId, text, nudge) {
    this.openFor = { threadId: String(threadId), text };
    this.name = typeof nudge.name === 'string' ? nudge.name : '';
    this.nameTokens = Array.isArray(nudge.name_tokens) ? nudge.name_tokens.filter((t) => typeof t === 'string') : [];
    this.asks = nudge.asks === true;
    this.title.textContent = nudge.title || '';
    this.prompt.textContent = nudge.prompt || '';
    this.renderAnywayLabel(text);
    this.renderChips(Array.isArray(nudge.suggestions) ? nudge.suggestions : []);
    this.root.hidden = false;
    this.input.setAttribute('aria-describedby', this.prompt.id || '');
    this.submit.setAttribute('aria-describedby', this.prompt.id || '');
    this.announce();
    window.LucideIcons?.initialize(this.root);
  }

  hide() {
    if (!this.isOpen()) {
      return;
    }
    const hadFocus = this.root.contains?.(document.activeElement) === true;
    this.root.hidden = true;
    this.openFor = null;
    this.input.removeAttribute('aria-describedby');
    this.submit.removeAttribute('aria-describedby');
    this.live?.replaceChildren();
    // A closed thread hides the composer first, and a hidden input takes no focus.
    if (hadFocus && this.input.offsetParent !== null) {
      this.input.focus();
    }
  }

  announce() {
    if (!this.live) {
      return;
    }
    // Clear first: a live region does not read the same text twice. One paragraph per line gives a pause.
    this.live.replaceChildren();
    setTimeout(() => {
      if (this.isOpen()) {
        const lines = [this.title.textContent, this.prompt.textContent, this.root.dataset?.srHint || ''];
        this.live.replaceChildren(...lines.filter(Boolean).map((line) => {
          const paragraph = document.createElement('p');
          paragraph.textContent = line;
          return paragraph;
        }));
      }
    }, 150);
  }

  renderAnywayLabel(text) {
    const [before, after] = splitAnywayTemplate(this.root.dataset?.anywayTemplate);
    const quoted = document.createElement('span');
    quoted.className = 'inline-block max-w-[12ch] truncate align-bottom group-hover:underline';
    // dir=auto isolates the sender's text in its own direction inside an RTL label.
    quoted.dir = 'auto';
    quoted.textContent = text;
    this.anywayLabel.replaceChildren(before, quoted, after);
  }

  renderChips(suggestions) {
    this.chips.replaceChildren();
    suggestions.forEach((suggestion) => {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'inline-flex items-center gap-1.5 max-w-full min-h-[44px] [@media(pointer:fine)]:min-h-[30px] px-3 rounded-full '
        // theme() values: see the nudge card in pm/index.twig.
        + 'border border-[theme(colors.sky.200)] bg-[theme(colors.white)] text-[13px] text-[theme(colors.gray.900)] '
        + 'shadow-[0_1px_2px_rgba(15,23,42,0.08)] hover:bg-[theme(colors.gray.50)] dark:border-sky-400/30 '
        + 'dark:bg-sky-900/40 dark:text-gray-100 dark:shadow-none dark:hover:bg-sky-900/70 focus:outline-none '
        + 'focus-visible:outline-offset-0 '
        + 'focus-visible:ring-2 focus-visible:ring-sky-600 dark:focus-visible:ring-sky-400 transition-colors';
      chip.setAttribute('aria-label', `${suggestion.label}: ${suggestion.text}`);

      const icon = document.createElement('i');
      icon.setAttribute('data-lucide', (KIND_ICONS[suggestion.kind] || KIND_ICONS.generic).icon);
      icon.className = 'w-3.5 h-3.5 flex-none text-sky-500 dark:text-sky-400';
      icon.setAttribute('aria-hidden', 'true');

      const label = document.createElement('span');
      label.className = 'truncate';
      label.dir = 'auto';
      label.textContent = suggestion.label;

      chip.append(icon, label);
      chip.addEventListener('click', () => this.applySuggestion(suggestion.text));
      this.chips.append(chip);
    });
  }

  applySuggestion(question) {
    // The composition tracker reads the source from beforeinput. A chip is a replacement, not a paste.
    if (typeof InputEvent === 'function') {
      this.input.dispatchEvent(new InputEvent('beforeinput', { bubbles: true, inputType: 'insertReplacementText' }));
    }
    const data = this.root.dataset || {};
    const threadId = this.openFor?.threadId;
    this.input.value = composeWithQuestion(this.input.value, question, this.name, {
      named: data.composeTemplate,
      unnamed: data.composeUnnamedTemplate,
      questionNamed: data.composeQuestionTemplate,
      questionUnnamed: data.composeQuestionUnnamedTemplate,
    }, this.nameTokens, this.asks);
    // The chip text asks a question, so a send of exactly that text goes out with no second check.
    if (threadId) {
      this.bypass.allow(threadId, this.input.value.trim());
    }
    this.input.dispatchEvent(new Event('input', { bubbles: true }));
    this.input.focus();
    const end = this.input.value.length;
    this.input.setSelectionRange(end, end);
  }

  sendAnyway() {
    if (!this.openFor) {
      return;
    }
    this.bypass.allow(this.openFor.threadId, this.openFor.text);
    this.hide();
    // A tap in iOS Safari does not focus the button, so hide() does not restore focus.
    this.input.focus();
    window.send_msg();
  }

  /**
   * A send while the nudge is open: read it again, and shake it, or ring it under reduced motion.
   */
  signalHeld() {
    this.announce();
    if (typeof this.root.animate !== 'function') {
      return;
    }
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      this.root.animate(
        [{ opacity: 1 }, { opacity: 0.55 }, { opacity: 1 }],
        { duration: 400, easing: 'ease-in-out' }
      );
      return;
    }
    this.root.animate(
      [
        { transform: 'translateX(0)' },
        { transform: 'translateX(-6px)' },
        { transform: 'translateX(6px)' },
        { transform: 'translateX(-4px)' },
        { transform: 'translateX(0)' },
      ],
      { duration: 280, easing: 'ease-in-out' }
    );
  }
}

function isMessageNode(node) {
  return node.nodeType === 1
    && (node.classList?.contains('pm_msg') || node.querySelector?.('.pm_msg') != null);
}

function isEmptyThread() {
  const wrapper = document.getElementById('messages_wrapper');
  return !!wrapper && wrapper.querySelector('.pm_msg') === null;
}

export function createOpenerNudge(apiClient) {
  const root = document.getElementById('opener_nudge');
  const input = document.getElementById('message');
  const submit = document.getElementById('msg_submit');
  if (!root || !input || !submit || !apiClient) {
    return null;
  }
  return new OpenerNudge({
    root,
    input,
    submit,
    apiClient,
    live: document.querySelector('[data-opener-nudge-live]'),
    threadIdInput: document.getElementById('send_thread_id'),
    messagesPane: document.getElementById('threads_right'),
  });
}
